'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import {
  DEFAULT_MAX_QUANTITY,
  QuantityPicker,
} from '@/ui/form/inputs/quantity-picker/QuantityPicker.client'
import { Icon } from '@/ui/icons/icon/Icon'
import type { FetchResult } from '@apollo/client'
import type { GraphQLFormattedError } from 'graphql'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { ProductAvailabilityIssueCard } from '../../product-availability-issue-card/ProductAvailabilityIssueCard.client'

const DEBOUNCE_TIMING = 300

export type ProductRowOnQuantityChangeInfos = {
  quantity: number
  delta: number
}

export type ProductRowControlsDelegateProps = {
  onQuantityChange?: (
    infos: ProductRowOnQuantityChangeInfos,
  ) => Promise<readonly GraphQLFormattedError[] | FetchResult | void>
  onQuantityChangeFailed?: () => void
  onDelete?: () => Promise<readonly GraphQLFormattedError[] | FetchResult | void>
  onDeleteFailed?: () => void
}

type ProductRowControlsProps = {
  step?: number
  itemQuantity: number
  itemQuantityMin?: number | null
  itemQuantityMax?: number | null
  productName: string
  isDisabled?: boolean
  availabilityIssue?: 'storeOnly' | 'noPricesAvailable' | 'isNotSellable' | null
} & ProductRowControlsDelegateProps

export function ProductRowControls({
  step,
  itemQuantity: itemQuantityProp,
  itemQuantityMin: minQuantity,
  itemQuantityMax,
  productName,
  isDisabled,
  availabilityIssue,
  ...rest
}: ProductRowControlsProps) {
  const { t } = useTranslation(['generic', 'productRow'])
  const maxQuantity = itemQuantityMax || DEFAULT_MAX_QUANTITY
  const itemQuantity = Math.min(itemQuantityProp, maxQuantity)
  const [quantity, setQuantity] = useState(itemQuantity)
  const [debouncedQuantity] = useDebounceValue(quantity, DEBOUNCE_TIMING)
  const [previousQuantity, setPreviousQuantity] = useState(itemQuantity)
  const updateRunning = useRef(false)
  // Flag used to ignore quantity updates coming from the server cache sync
  const isSyncingFromProps = useRef(false)

  useEffect(() => {
    isSyncingFromProps.current = true
    setQuantity(itemQuantity)
    setPreviousQuantity(itemQuantity)
  }, [itemQuantity])

  const { onQuantityChange, onQuantityChangeFailed, onDelete, onDeleteFailed } = rest

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      if (isDisabled) return
      setQuantity(newQuantity)
    },
    [isDisabled],
  )

  const updateQuantityEffect = useCallback(async () => {
    if (isSyncingFromProps.current) {
      if (debouncedQuantity === itemQuantity) {
        isSyncingFromProps.current = false
      }

      return
    }

    if (
      debouncedQuantity === previousQuantity ||
      isDisabled ||
      !onQuantityChange ||
      updateRunning.current
    )
      return

    updateRunning.current = true

    try {
      const errors = await onQuantityChange({
        quantity: debouncedQuantity,
        delta: debouncedQuantity - previousQuantity,
      })

      if (errors && 'length' in errors && errors?.length) {
        onQuantityChangeFailed?.()
        setQuantity(previousQuantity)
      } else {
        setPreviousQuantity(debouncedQuantity)
      }
    } catch {
      onQuantityChangeFailed?.()
      setQuantity(previousQuantity)
    } finally {
      updateRunning.current = false
    }
  }, [
    debouncedQuantity,
    itemQuantity,
    onQuantityChange,
    previousQuantity,
    onQuantityChangeFailed,
    isDisabled,
  ])

  useEffect(() => {
    updateQuantityEffect()
  }, [updateQuantityEffect])

  const deleteCartItem = useCallback(async () => {
    if (isDisabled || !onDelete) return

    try {
      const errors = await onDelete()

      if (errors && 'length' in errors && errors?.length) {
        onDeleteFailed?.()
      }
    } catch {
      onDeleteFailed?.()
    }
  }, [onDelete, onDeleteFailed, isDisabled])

  return (
    <div className={twMerge('flex items-center gap-2', availabilityIssue && 'justify-start')}>
      {availabilityIssue ? (
        <div className="flex h-auto flex-col justify-center gap-1">
          <ProductAvailabilityIssueCard compact issue={availabilityIssue} />
        </div>
      ) : (
        <QuantityPicker
          size="sm"
          value={quantity}
          maxQuantity={maxQuantity}
          minQuantity={minQuantity}
          isDisabled={isDisabled}
          onQuantityChange={handleQuantityChange}
          step={step}
          intent={INTENT_SAME_AS_WEB}
        />
      )}
      {onDelete && (
        <Button
          size="md"
          variant="tertiary"
          isRound
          iconLeft={<Icon.Trash2 className="text-brand-2-900" />}
          onPress={deleteCartItem}
          isDisabled={isDisabled}
          intent={INTENT_SAME_AS_WEB}
        >
          {t('productRow:action.remove.label', { productName: productName || '' })}
        </Button>
      )}
    </div>
  )
}
